import React, { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button, Col, Form, Input, Modal, Row, Select } from 'antd';

import { ChevronDown, ChevronUp, CloseIcon } from '../../../../assets/icons';

import FloatingLabel from '../../../../components/FloatingLabel/FloatingLabel';
import { UploadField } from '../../../../components/SharedComponents/FormComponents/UploadField';
import { nameChange } from '../../../../services/AccountApi/profileApi';
import { changeLegalName } from '../../../../store/reducer/accountSlice';
import { uploadIdentificationDocuments } from '../../../../utils/identificationDocuments';
import { toastNotify } from '../../../../utils/toastNotify';
import { FIRST_NAME_LENGTHS, FIRST_NAME_VALIDATION, LAST_NAME_LENGTHS, LAST_NAME_VALIDATION } from '../../../../utils/validationMessages';
import { alphabetSpaceRegex, onlyAlphabetRegex } from '../../../../utils/Validations';
import { useIdentificationUpload } from '../../../../Hooks/useIdentificationUpload';

import './Modals.scss';

const NameChange = (props) => {
  const userData = useSelector((state) => state.userData.userData);
  const [firstName, setFirstName] = useState();
  const [lastName, setLastName] = useState();
  const [loading, setLoading] = useState(false);
  const [arrowUp, setArrowUp] = useState(false);
  const [form] = Form.useForm();

  const {
    identificationType,
    fileListFront,
    fileListBack,
    fileListPassport,
    fileValidate,
    fileSizeValidate,
    fileFrontValidate,
    fileFrontSizeValidate,
    fileBackValidate,
    fileBackSizeValidate,
    onChangeFront,
    onChangeBack,
    onChangePassport,
    beforeUpload,
    handleRemoveUploaded,
    handleIdentificationChange,
    buildUploadsData,
    isUploadInvalid,
    isUploadIncomplete,
  } = useIdentificationUpload();

  const dispatch = useDispatch();

  const handleUpdateUserName = async (formBodyData) => {
    try {
      const resp = await nameChange(formBodyData);
      if (!resp.isError) {
        toastNotify('success', resp?.message);
        //store name changed into redux
        dispatch(
          changeLegalName({
            firstName: formBodyData.firstName,
            lastName: formBodyData.lastName,
          })
        );
        props.setClose();
      } else {
        toastNotify('error', resp?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    }
    setLoading(false);
  };

  const onNameChangeSubmit = async (values) => {
    setLoading(true);
    const formData = {
      firstName: values.firstName?.trim() ?? '',
      lastName: values.lastName,
    };

    try {
      const consolidatedDocs = await uploadIdentificationDocuments(buildUploadsData());

      if (Object.keys(consolidatedDocs).length > 0) {
        await handleUpdateUserName({ ...formData, docs: consolidatedDocs });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in upload process:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal className='namechangemodal' open={props.setOpen} onCancel={props.setClose} closeIcon={<CloseIcon />} footer={false} width={524} centered>
      <h4 className='mb-32 text-center'>Name change identity verification</h4>
      <p className='mb-24'>In order to process your name change request we are required by federal law to verify your identity. </p>
      <p className='fw-600 mb-4'>
        {userData?.firstName} {userData?.lastName}
      </p>
      <p className='mb-24'>Enter your first and last name <b>as it appears on your government issued ID.</b></p>
      <Form className='namechangeform' name='namechangeform' form={form} onFinish={onNameChangeSubmit}>
        <Row gutter={[16, 0]}>
          <Col span={24} md={12}>
            <FloatingLabel label={
              <>
              <b>Legal</b> First Name
              </>
            } inputId='firstName' inputValue={firstName}>
              <Form.Item
                validateTrigger='onBlur'
                name='firstName'
                rules={[
                  { required: true, message: FIRST_NAME_VALIDATION.REQUIRED },
                  {
                    transform: (value) => value?.trim(),
                    max: FIRST_NAME_LENGTHS.MAX,
                    message: FIRST_NAME_VALIDATION.MAX_LENGTH,
                  },
                  {
                    transform: (value) => value?.trim(),
                    min: FIRST_NAME_LENGTHS.MIN,
                    message: FIRST_NAME_VALIDATION.MIN_LENGTH,
                  },
                  {
                    pattern: alphabetSpaceRegex,
                    message: FIRST_NAME_VALIDATION.FORMAT,
                  },
                ]}
              >
                <Input id='firstName' value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </Form.Item>
            </FloatingLabel>
          </Col>
          <Col span={24} md={12}>
            <FloatingLabel
            label={
            <>
              <b>Legal</b> Last Name
            </>
            }
            inputId='lastName' inputValue={lastName}>
              <Form.Item
                validateTrigger='onBlur'
                name='lastName'
                rules={[
                  {
                    required: true,
                    message: LAST_NAME_VALIDATION.REQUIRED,
                  },
                  {
                    max: LAST_NAME_LENGTHS.MAX,
                    message: LAST_NAME_VALIDATION.MAX_LENGTH,
                  },
                  {
                    min: LAST_NAME_LENGTHS.MIN,
                    message: LAST_NAME_VALIDATION.MIN_LENGTH,
                  },
                  {
                    pattern: onlyAlphabetRegex,
                    message: LAST_NAME_VALIDATION.FORMAT,
                  },
                ]}
              >
                <Input id='lastName' value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Form.Item>
            </FloatingLabel>
          </Col>
        </Row>
        <hr className='mb-24' />
        <p>Please upload a valid government issued ID, must be clear and legible. (JPEG, JPG, PNG files only)</p>
        <FloatingLabel label='Type of identification' inputId='identification' inputValue={identificationType}>
          <>
            <div id='identification-anchor' />
            <Form.Item>
              <Select
                id='identification'
                onChange={handleIdentificationChange}
                value={identificationType}
                getPopupContainer={() => document.getElementById('identification-anchor')}
                onDropdownVisibleChange={() => setArrowUp(!arrowUp)}
                suffixIcon={arrowUp ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
              >
                <Select.Option value='passport'>Passport</Select.Option>
                <Select.Option value='license'>ID</Select.Option>
              </Select>
            </Form.Item>
          </>
        </FloatingLabel>
        {identificationType === 'license' && (
          <Row gutter={16}>
            <Col span={24} md={12}>
              <Form.Item className='license-front'>
                <UploadField
                  className=''
                  onChangeHandle={onChangeFront}
                  fileList={fileListFront}
                  id='licenseFront'
                  beforeUpload={(value) => beforeUpload(value, 'front')}
                  handleRemoveUploaded={() => handleRemoveUploaded(fileListFront[0].uid, 'front')}
                  label={'Upload front of ID'}
                  fileValidate={fileFrontValidate}
                  fileSizeValidate={fileFrontSizeValidate}
                  loading={loading}
                />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item className='license-back'>
                <UploadField
                  className=''
                  onChangeHandle={onChangeBack}
                  fileList={fileListBack}
                  id='licenseBack'
                  beforeUpload={(value) => beforeUpload(value, 'back')}
                  label={'Upload back of ID'}
                  handleRemoveUploaded={() => handleRemoveUploaded(fileListBack[0].uid, 'back')}
                  fileValidate={fileBackValidate}
                  fileSizeValidate={fileBackSizeValidate}
                  loading={loading}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        {identificationType === 'passport' && (
          <Row>
            <Col span={24} md={12}>
              <Form.Item className='passport'>
                <UploadField
                  className=''
                  onChangeHandle={onChangePassport}
                  fileList={fileListPassport}
                  id='passport'
                  beforeUpload={(value) => beforeUpload(value, 'passport')}
                  handleRemoveUploaded={() => !loading && handleRemoveUploaded(fileListPassport[0].uid, 'passport')}
                  label={'Upload front of ID'}
                  fileValidate={fileValidate}
                  fileSizeValidate={fileSizeValidate}
                  loading={loading}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        <p className='fz-12 text-taupe-80 mb-32'>
          Still have questions?{' '}
          <Link className='underline fw-600 text-taupe-80' to='/account/help-center'>
            Contact Realbricks Customer Support
          </Link>
        </p>
        <Form.Item className='mb-0' shouldUpdate>
          {() => (
            <Button
              className='w-100'
              type='primary'
              htmlType='submit'
              loading={loading}
              disabled={!form.isFieldsTouched(true) || !!form.getFieldsError().filter(({ errors }) => errors.length).length || isUploadIncomplete || isUploadInvalid}
            >
              Confirm
            </Button>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NameChange;
